import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ClaudeTask, ClaudeTasksStatistics } from '@/types/claudeTask';
import { sortPeriods, deriveYearsFromQuarters } from '@/utils/periodUtils';
import { supabase } from '@/integrations/supabase/client';

// Response type for claudeTasks query
interface ClaudeTasksResponse {
  claudeTasks: {
    tasks: ClaudeTask[];
    totalCount: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

/**
 * Hook for fetching a single Claude task by ID using direct Supabase query
 */
export function useClaudeTask(taskId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['claudeTask', taskId],
    queryFn: async (): Promise<any> => {
      const { data, error } = await supabase
        .from('claude_tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (error) {
        console.error('[useClaudeTask] Error fetching task:', error);
        throw error;
      }

      return data;
    },
    enabled: enabled && !!taskId,
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache
    retry: 1, // Only retry once
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching Claude tasks with filters and pagination
 * Uses direct Supabase queries instead of GraphQL
 */
export function useClaudeTasks(params?: {
  slug?: string;
  command?: string;
  status?: string[];
  created_after?: string;
  periods?: string[];
  limit?: number;
  offset?: number;
}, options?: UseQueryOptions<ClaudeTasksResponse>) {
  return useQuery({
    queryKey: ['claudeTasks', params],
    queryFn: async (): Promise<ClaudeTasksResponse> => {
      // Build query with filters (excluding period filter - will be done client-side)
      let query = supabase
        .from('claude_tasks')
        .select('*', { count: 'exact' })
        .order('updated_at', { ascending: false });  // Most recently updated first

      // Apply filters
      if (params?.slug) {
        query = query.eq('slug', params.slug);
      }
      if (params?.command) {
        query = query.eq('command', params.command);
      }
      if (params?.status && params.status.length > 0) {
        query = query.in('status', params.status);
      }
      if (params?.created_after) {
        query = query.gte('created_at', params.created_after);
      }

      // Fetch more data if we're filtering by periods (to ensure we have enough after client-side filtering)
      // Otherwise use the requested pagination
      const limit = params?.limit || 20;
      const offset = params?.offset || 0;

      // If filtering by periods, fetch a larger batch to ensure we have enough results after filtering
      const fetchLimit = (params?.periods && params.periods.length > 0) ? limit * 5 : limit;
      const fetchOffset = (params?.periods && params.periods.length > 0) ? offset * 5 : offset;

      query = query.range(fetchOffset, fetchOffset + fetchLimit - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error('[useClaudeTasks] Error fetching tasks:', error);
        throw error;
      }

      let tasks = (data || []) as ClaudeTask[];
      const hasPeriodFilter = params?.periods && params.periods.length > 0;

      // Apply client-side period filtering if periods are specified
      if (hasPeriodFilter) {
        tasks = tasks.filter(task => {
          const taskPeriod = task.params?.period;
          return taskPeriod && params.periods!.includes(taskPeriod);
        });
      }

      // Apply client-side pagination after filtering
      const paginatedTasks = hasPeriodFilter ? tasks.slice(offset, offset + limit) : tasks;

      // Use database count when no period filter, otherwise use filtered count
      const totalCount = hasPeriodFilter ? tasks.length : (count || 0);

      return {
        claudeTasks: {
          tasks: paginatedTasks,
          totalCount,
          hasNextPage: offset + limit < totalCount,
          hasPrevPage: offset > 0,
        }
      };
    },
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache
    retry: 1, // Only retry once
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: (data) => {
      // Auto-refresh if there are pending or running tasks
      if (!data) return false;
      const hasPendingOrRunning = data?.claudeTasks?.tasks?.some(
        task => task.status === 'pending' || task.status === 'running'
      );
      return hasPendingOrRunning ? 3000 : false; // 3 seconds
    },
    ...options
  });
}

/**
 * Hook for fetching Claude tasks statistics using direct Supabase count queries
 * When period filtering is active, fetches tasks and filters client-side
 */
export function useClaudeTasksStatistics(params?: {
  slug?: string;
  command?: string;
  status?: string[];
  created_after?: string;
  periods?: string[];
}) {
  return useQuery({
    queryKey: ['claudeTasksStatistics', params],
    queryFn: async (): Promise<ClaudeTasksStatistics> => {
      const hasPeriodFilter = params?.periods && params.periods.length > 0;

      // Build a base query with all common filters (used for both period and non-period paths)
      const buildBaseCountQuery = (statusValue?: string) => {
        let q = supabase.from('claude_tasks').select('*', { count: 'exact', head: true });
        if (params?.slug) q = q.eq('slug', params.slug);
        if (params?.command) q = q.eq('command', params.command);
        if (params?.created_after) q = q.gte('created_at', params.created_after);
        if (hasPeriodFilter) {
          // Server-side JSON filter on params->>'period' — avoids the 1000-row client-side cap
          if (params!.periods!.length === 1) {
            q = q.filter('params->>period', 'eq', params!.periods![0]);
          } else {
            q = q.or(params!.periods!.map(p => `params->>period.eq.${p}`).join(','));
          }
        }
        if (statusValue) q = q.eq('status', statusValue);
        return q;
      };

      if (hasPeriodFilter) {
        // Use server-side count queries with JSON period filter (same approach as non-period path)
        const [
          totalResult, pendingResult, runningResult, completedResult, failedResult,
          runDiffResult, scrapeResult, analyzeResult, runExtractResult, fixDiffResult, tagPageResult, discoveryResult
        ] = await Promise.all([
          buildBaseCountQuery(),
          buildBaseCountQuery('pending'),
          buildBaseCountQuery('running'),
          buildBaseCountQuery('completed'),
          buildBaseCountQuery('failed'),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'run_diff'); return q; })(),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'scrape'); return q; })(),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'analyze'); return q; })(),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'run_extract'); return q; })(),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'fix_diff'); return q; })(),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'tag_page'); return q; })(),
          (() => { let q = buildBaseCountQuery(); if (!params?.command) q = q.eq('command', 'discovery'); return q; })(),
        ]);

        if (totalResult.error) throw totalResult.error;

        return {
          totalTasks: totalResult.count || 0,
          pending: pendingResult.count || 0,
          running: runningResult.count || 0,
          completed: completedResult.count || 0,
          failed: failedResult.count || 0,
          byCommand: {
            run_diff: runDiffResult.count || 0,
            scrape: scrapeResult.count || 0,
            analyze: analyzeResult.count || 0,
            run_extract: runExtractResult.count || 0,
            fix_diff: fixDiffResult.count || 0,
            tag_page: tagPageResult.count || 0,
            discovery: discoveryResult.count || 0,
          }
        };
      } else {
        // No period filter - use efficient count queries
        const buildQuery = (statusValue?: string, commandValue?: string) => {
          let q = supabase.from('claude_tasks').select('*', { count: 'exact', head: true });
          if (params?.slug) q = q.eq('slug', params.slug);
          if (params?.command) q = q.eq('command', params.command);
          if (params?.created_after) q = q.gte('created_at', params.created_after);
          if (statusValue) q = q.eq('status', statusValue);
          if (commandValue) q = q.eq('command', commandValue);
          return q;
        };

        // Run all count queries in parallel
        const [
          totalResult,
          pendingResult,
          runningResult,
          completedResult,
          failedResult,
          runDiffResult,
          scrapeResult,
          analyzeResult,
          runExtractResult,
          fixDiffResult,
          tagPageResult,
          discoveryResult
        ] = await Promise.all([
          buildQuery(),
          buildQuery('pending'),
          buildQuery('running'),
          buildQuery('completed'),
          buildQuery('failed'),
          buildQuery(undefined, 'run_diff'),
          buildQuery(undefined, 'scrape'),
          buildQuery(undefined, 'analyze'),
          buildQuery(undefined, 'run_extract'),
          buildQuery(undefined, 'fix_diff'),
          buildQuery(undefined, 'tag_page'),
          buildQuery(undefined, 'discovery'),
        ]);

        return {
          totalTasks: totalResult.count || 0,
          pending: pendingResult.count || 0,
          running: runningResult.count || 0,
          completed: completedResult.count || 0,
          failed: failedResult.count || 0,
          byCommand: {
            run_diff: runDiffResult.count || 0,
            scrape: scrapeResult.count || 0,
            analyze: analyzeResult.count || 0,
            run_extract: runExtractResult.count || 0,
            fix_diff: fixDiffResult.count || 0,
            tag_page: tagPageResult.count || 0,
            discovery: discoveryResult.count || 0
          }
        };
      }
    },
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache
    retry: 1, // Only retry once
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: (data) => {
      return data ? 3000 : false;
    },
  });
}

function getRecentISOWeeks(count: number): string[] {
  const now = new Date();
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i * 7));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    result.push(`${d.getUTCFullYear()}W${String(weekNum).padStart(2, '0')}`);
  }
  return result;
}

/**
 * Hook for fetching available periods.
 * Always includes the last 8 ISO weeks so the current week appears even before any diffs publish.
 * Merges with periods from app_news (published diffs) for historical coverage.
 */
export function useAvailablePeriods() {
  return useQuery({
    queryKey: ['availablePeriods'],
    queryFn: async (): Promise<string[]> => {
      const periods = new Set<string>();

      // Always seed with the last 8 ISO weeks so current week is always present
      getRecentISOWeeks(8).forEach(w => periods.add(w));

      const batchSize = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('app_news')
          .select('period')
          .not('period', 'is', null)
          .range(offset, offset + batchSize - 1);

        if (error) {
          console.error('[useAvailablePeriods] Error fetching periods:', error);
          throw error;
        }

        data?.forEach((row: any) => {
          if (row.period && typeof row.period === 'string') {
            periods.add(row.period);
          }
        });

        if (!data || data.length < batchSize) {
          hasMore = false;
        } else {
          offset += batchSize;
        }
      }

      const uniquePeriods = Array.from(periods);
      const sortedPeriods = sortPeriods(uniquePeriods);

      const quarters = sortedPeriods.filter(p => p.includes('Q'));
      const years = deriveYearsFromQuarters(quarters);

      return [...sortedPeriods, ...years];
    },
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 5,
    retry: 1,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}
